/**
 * A paired kid device. The pairing lives in an HttpOnly cookie the page never sees; we only ask
 * the API who we are. When paired, spider-hunt progress is saved to the kid's profile and
 * localStorage is a cache; signed out, localStorage is the only copy (packages/ui/src/spider.ts).
 */
import { mergeProgress, type ApiError, type KidProfile, type KidProgress } from '@ka/contracts';
import { readProgress, writeProgress } from '@ka/ui';

let kid: KidProfile | null = null;
let applying = false;
// Hint only (the cookie is the credential): lets unpaired visitors skip the API call entirely.
const HINT = 'ka.kid.paired';
const hint = (on: boolean) => {
  try {
    if (on) localStorage.setItem(HINT, '1');
    else localStorage.removeItem(HINT);
  } catch {
    /* ignore */
  }
};
const hinted = () => {
  try {
    return localStorage.getItem(HINT) === '1';
  } catch {
    return false;
  }
};

export async function kidApi<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/kid${path}`, {
    method,
    headers: { 'content-type': 'application/json', 'x-ka-csrf': '1' },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
  });
  const json = (await res.json().catch(() => null)) as unknown;
  if (!res.ok)
    throw Object.assign(
      new Error((json as ApiError | null)?.error.message ?? 'Something went wrong.'),
      { status: res.status },
    );
  return json as T;
}

function apply(p: KidProgress | null) {
  if (!p) return;
  applying = true;
  try {
    writeProgress(p);
  } finally {
    applying = false;
  }
}

export const currentKid = () => kid;

/** On page load: if paired, the profile's progress replaces this browser's copy. */
export async function loadKid(): Promise<KidProfile | null> {
  if (!hinted()) return null;
  try {
    kid = await kidApi<KidProfile>('GET', '/me');
  } catch (e) {
    kid = null;
    if ((e as { status?: number }).status === 401) hint(false); // revoked or expired
  }
  if (kid) apply(kid.progress);
  return kid;
}

/** Pairs this device; progress already found in this browser is merged into the profile. */
export async function pairDevice(code: string, localVersion: number): Promise<KidProfile> {
  kid = await kidApi<KidProfile>('POST', '/pair', { code });
  hint(true);
  const local = readProgress(localVersion);
  const merged = mergeProgress(kid.progress, local.found.length ? local : null);
  if (merged && JSON.stringify(merged) !== JSON.stringify(kid.progress))
    kid = await kidApi<KidProfile>('PUT', '/progress', merged);
  apply(kid.progress);
  return kid;
}

export async function unpairDevice(): Promise<void> {
  await kidApi('POST', '/unpair');
  kid = null;
  hint(false);
}

/** Saves every local change (find, reset) to the profile while paired. */
export function startProgressSync() {
  window.addEventListener('ka-secret-progress', (e) => {
    if (!kid || applying) return;
    const p = (e as CustomEvent<KidProgress>).detail;
    void kidApi<KidProfile>('PUT', '/progress', p)
      .then((k) => (kid = k))
      .catch(() => undefined); // offline: the local copy still has it; next find retries
  });
}
